import { type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import '../consts/events.quiz-classroom-quiz';
import '../consts/events.quiz-classroom-link-nudge';
import { trackEvent } from '@zep/logger/tracking';
import { zusToast } from '@zep/toast';
import { type SaveContentLinksReq } from '@/api/gen/_model/saveContentLinksReq';
import { useSaveContentLinks } from '@/api/gen/classroom/classroom.gen';
import { ZepQuizApiError } from '@/api/httpClient';
import { useJoinWithLocale } from '@/hooks';
import { SuccessToast } from '@/shared/components';
import { CLASSROOM_ROUTES } from '@/shared/consts/routes';
import { QUIZ_SAVE_TOAST_POSITION } from '@/shared/features/quiz-folder/utils/quizSaveFeedback';
import { ClassroomContentLinkedToast } from '../components/ClassroomContentLinkedToast';
import { type ContentShareType } from '../types';
import { classroomContentToken, type ClassroomSource, handleClassroomLinkError, invalidateClassroomListQueries, invalidateClassroomMutationQueries, openClassroomTab, trackClassroomEvent } from '../utils';
type SaveResult = 'linkedAndUnlinked' | 'unlinked' | 'linkedOne' | 'linkedMany';

/** 저장 결과의 종류. 어떤 토스트를 띄울지는 이 값 하나로 정한다. */
export const getSaveResult = (linked: string[], unlinked: string[]): SaveResult => {
  if (linked.length > 0 && unlinked.length > 0) return 'linkedAndUnlinked';
  if (unlinked.length > 0) return 'unlinked';
  return linked.length === 1 ? 'linkedOne' : 'linkedMany';
};

/** 서버 에러 코드(`CLASS_LINK_QUIZSET_LOCKED` 등). 계측 reason 과 안내 분기에 쓴다. */
const errorStatusOf = (error: unknown) => error instanceof ZepQuizApiError ? error.status : undefined;
type LinkChanges = Pick<SaveContentLinksReq, 'linkClassGroupIds' | 'unlinkClassGroupIds' | 'newClassGroupName'>;

/**
 * 클래스 연결 창의 「저장」/「만들고 연결하기」.
 * 생성·연결·해제를 서버 한 트랜잭션으로 보내서 전부 반영되거나 전부 반영되지 않는다.
 * 계측은 단건 연결(`useClassroomLinking`)과 같은 이벤트를 클래스마다 한 번씩 낸다 —
 * 대시보드가 창 교체 전후로 이어지게.
 */
export function useSaveClassroomLinks({
  contentId,
  contentType,
  source
}: {
  contentId: string;
  contentType: ContentShareType;
  source: ClassroomSource;
}) {
  const queryClient = useQueryClient();
  const {
    t
  } = useTranslation(['common']);
  const classroomPath = useJoinWithLocale();
  const contentToken = classroomContentToken(contentType);

  /** `click/success/fail_{link|unlink}_classroom_{quiz|board}` — 기존 단건 연결과 같은 이름. */
  const trackLinkEvent = (phase: 'click' | 'success' | 'fail', operation: 'link' | 'unlink', classroomIds: string[], errorStatus?: string) => classroomIds.forEach(classroomId => trackClassroomEvent(`${phase}_${operation}_classroom_${contentToken}`, {
    classroomId,
    contentId,
    source,
    ...(errorStatus && {
      errorStatus
    })
  }));
  const trackSave = (classroomIds: string[], operation: 'link' | 'unlink', result: 'success' | 'fail', reason?: string) => {
    if (contentType !== 'QUIZSET') return;
    classroomIds.forEach(classroomId => trackEvent('quiz_classroom_quiz_save', {
      classroom_id: classroomId,
      quiz_set_id: contentId,
      operation,
      result,
      place: source,
      ...(reason && {
        reason
      })
    }));
  };

  /**
   * 저장 1회당 1건. 서버가 생성·연결·해제를 한 트랜잭션으로 처리하므로 요청 값이 곧 결과다.
   * 클래스 id 가 없는 「새로 만들고 연결」 실패도 여기서만 남는다(`trackSave` 는 클래스마다 돈다).
   */
  const trackModalSave = (changes: LinkChanges, result: 'success' | 'fail', reason?: string) => {
    if (contentType !== 'QUIZSET') return;
    trackEvent('quiz_classroom_link_modal_save', {
      place: source,
      quiz_set_id: contentId,
      link_count: changes.linkClassGroupIds.length,
      unlink_count: changes.unlinkClassGroupIds.length,
      created_new_class: !!changes.newClassGroupName,
      result,
      ...(reason && {
        reason
      })
    });
  };
  const showResultToast = (linked: string[], unlinked: string[]) => {
    const toastByResult: Record<SaveResult, () => ReactElement> = {
      linkedAndUnlinked: () => <SuccessToast message={t('common:classroomLink.savedMessage')} />,
      unlinked: () => <SuccessToast message={t('common:classroomLink.unlinkedMessage')} />,
      linkedOne: () => <ClassroomContentLinkedToast classroomId={linked[0]} contentType={contentType} classroomPath={classroomPath} />,
      // 여러 클래스에 한 번에 연결하면 「클래스로 이동」은 클래스 목록으로 보낸다.
      linkedMany: () => <SuccessToast message={contentType === 'QUIZSET' ? t('common:classroomLink.quizLinkedMessage') : t('common:classroomLink.boardLinkedMessage')} action={{
        label: t('common:classroomLink.goToClassroomButton'),
        onClick: () => openClassroomTab(classroomPath(CLASSROOM_ROUTES.list()))
      }} />
    };
    // 토스트 칸은 내용 폭만큼 줄어 틀(360px) 왼쪽에 붙는다. 「저장했어요」처럼 짧은 토스트가
    // 왼쪽으로 쏠리지 않게 ZUS Toast 와 같은 폭을 주고 그 안에서 가운데 맞춘다.
    zusToast.custom(<div className="flex w-[calc(100vw-32px)] max-w-[360px] justify-center">
        {toastByResult[getSaveResult(linked, unlinked)]()}
      </div>, {
      duration: 2500,
      // 피그마 연결 창 토스트는 성공·실패 모두 저장 결과 토스트 자리 (10/21 지민).
      position: QUIZ_SAVE_TOAST_POSITION
    });
  };
  const mutation = useSaveContentLinks({
    mutation: {
      onSuccess: async ({
        data
      }, {
        data: variables
      }) => {
        trackModalSave(variables, 'success');
        trackSave(data.linkedClassGroupIds, 'link', 'success');
        trackSave(data.unlinkedClassGroupIds, 'unlink', 'success');
        trackLinkEvent('success', 'link', data.linkedClassGroupIds);
        trackLinkEvent('success', 'unlink', data.unlinkedClassGroupIds);
        showResultToast(data.linkedClassGroupIds, data.unlinkedClassGroupIds);
        await Promise.all([...[...data.linkedClassGroupIds, ...data.unlinkedClassGroupIds].map(id => invalidateClassroomMutationQueries(queryClient, id)), variables.newClassGroupName ? invalidateClassroomListQueries(queryClient) : undefined]);
      },
      onError: (error, {
        data: variables
      }) => {
        const errorStatus = errorStatusOf(error);
        trackModalSave(variables, 'fail', errorStatus);
        trackSave(variables.linkClassGroupIds, 'link', 'fail', errorStatus);
        trackSave(variables.unlinkClassGroupIds, 'unlink', 'fail', errorStatus);
        trackLinkEvent('fail', 'link', variables.linkClassGroupIds, errorStatus);
        trackLinkEvent('fail', 'unlink', variables.unlinkClassGroupIds, errorStatus);
        // 잠긴 퀴즈처럼 다시 해도 같은 결과인 에러는 전용 안내가 먼저. 그 외만 일반 토스트.
        // 전용 안내도 다른 결과 토스트와 같은 자리에 띄운다.
        if (handleClassroomLinkError(error, key => t(key as never), QUIZ_SAVE_TOAST_POSITION)) return;
        zusToast.error(t('common:classroomLink.saveFailedMessage'), {
          position: QUIZ_SAVE_TOAST_POSITION
        });
      }
    }
  });
  const save = (changes: LinkChanges) => {
    trackLinkEvent('click', 'link', changes.linkClassGroupIds);
    trackLinkEvent('click', 'unlink', changes.unlinkClassGroupIds);
    return mutation.mutateAsync({
      data: {
        contentId,
        contentType,
        ...changes
      }
    });
  };
  return {
    save,
    isPending: mutation.isPending
  };
}